import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlameIcon, MessageCircleIcon, SendIcon } from 'lucide-react';
import { Rant } from '../types';
import { formatRelativeTime, getCategoryInfo, generateId } from '../utils/rantUtils';
import { renderTextWithLinks } from '../utils/textUtils';
import { updateRantVote } from '../services/rantService';
import { getRepliesForRant, saveReplyToDatabase } from '../services/replyService';
import { ReplyCard } from './ReplyCard';
interface RantCardProps {
  rant: Rant;
  isRantOfWeek?: boolean;
  isSpicyTake?: boolean;
  isUniversalTruth?: boolean;
  onRantUpdated?: (rantId: string, updates: Partial<Rant>) => void;
}
export const RantCard: React.FC<RantCardProps> = ({
  rant,
  isRantOfWeek = false,
  isSpicyTake = false,
  isUniversalTruth = false,
  onRantUpdated
}) => {
  const {
    emoji,
    label
  } = getCategoryInfo(rant.category);
  const relativeTime = formatRelativeTime(rant.timestamp);
  // Local state for optimistic updates
  const [localVotes, setLocalVotes] = useState(rant.votes);
  const [localHasVoted, setLocalHasVoted] = useState(rant.hasVoted);
  const [isVoting, setIsVoting] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState<any[]>([]);
  const [replyText, setReplyText] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [replyCount, setReplyCount] = useState(rant.replyCount || 0);
  // Update local state when rant prop changes
  useEffect(() => {
    setLocalVotes(rant.votes);
    setLocalHasVoted(rant.hasVoted);
    setReplyCount(rant.replyCount || 0);
  }, [rant.votes, rant.hasVoted, rant.replyCount]);
  // Only load replies when user explicitly expands the replies section
  useEffect(() => {
    if (showReplies && replies.length === 0) {
      loadReplies();
    }
  }, [showReplies]);
  const loadReplies = async () => {
    const loadedReplies = await getRepliesForRant(rant.id);
    setReplies(loadedReplies);
    setReplyCount(loadedReplies.length);
  };
  const handleVote = async () => {
    if (localHasVoted || isVoting) return;
    setIsVoting(true);
    // Optimistic update
    setLocalVotes(localVotes + 1);
    setLocalHasVoted(true);
    try {
      await updateRantVote(rant.id);
      // Notify parent of the update
      if (onRantUpdated) {
        onRantUpdated(rant.id, {
          votes: localVotes + 1,
          hasVoted: true
        });
      }
    } catch (error) {
      console.error('Failed to vote:', error);
      // Revert optimistic update on error
      setLocalVotes(localVotes);
      setLocalHasVoted(false);
    } finally {
      setIsVoting(false);
    }
  };
  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (replyText.trim().length === 0) return;
    setIsSubmittingReply(true);
    try {
      const newReply = {
        id: generateId(),
        rantId: rant.id,
        text: replyText,
        timestamp: Date.now(),
        votes: 0
      };
      await saveReplyToDatabase(newReply);
      setReplyText('');
      await loadReplies();
      // Update reply count locally and notify parent
      const newReplyCount = replyCount + 1;
      setReplyCount(newReplyCount);
      if (onRantUpdated) {
        onRantUpdated(rant.id, {
          replyCount: newReplyCount
        });
      }
    } catch (error) {
      console.error('Failed to submit reply:', error);
      alert('Failed to submit reply. Please try again.');
    } finally {
      setIsSubmittingReply(false);
    }
  };
  return <motion.div className="rant-card bg-background-light rounded-lg p-6 mb-4" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} data-id="element-109">
      <div className="flex flex-wrap gap-2 mb-2" data-id="element-110">
        <span className="category-badge" data-id="element-111">
          {emoji} {label}
        </span>
        {isRantOfWeek && <span className="category-badge bg-amber-900/20 text-amber-500" data-id="element-112">
            🏆 Rant of the Week
          </span>}
        {isSpicyTake && <span className="category-badge bg-red-900/20 text-red-500" data-id="element-113">
            🌶️ Spicy Take
          </span>}
        {isUniversalTruth && <span className="category-badge bg-blue-900/20 text-blue-400" data-id="element-114">
            💯 Universal Truth
          </span>}
      </div>
      <p className="text-lg my-4" data-id="element-115">{renderTextWithLinks(rant.text)}</p>
      <div className="flex justify-between items-center mt-4" data-id="element-116">
        <div className="flex items-center gap-3" data-id="element-117">
          <motion.button onClick={handleVote} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border-2 transition-all cursor-pointer ${localHasVoted ? 'bg-primary/20 text-primary border-primary cursor-default' : 'bg-background border-gray-700 hover:border-primary hover:bg-primary/10 hover:text-primary'}`} whileHover={{
          scale: localHasVoted ? 1 : 1.05,
          boxShadow: localHasVoted ? 'none' : '0 0 20px rgba(255, 69, 0, 0.3)'
        }} whileTap={{
          scale: localHasVoted ? 1 : 0.95
        }} disabled={localHasVoted || isVoting} data-id="element-118">
            <FlameIcon className="h-5 w-5" data-id="element-119" />
            <span data-id="element-120">
              {localHasVoted ? 'FIRED UP' : isVoting ? 'FIRING...' : 'FIRE IT UP'}
            </span>
          </motion.button>
          <div className="flex items-center" data-id="element-121">
            {Array.from({
            length: Math.min(5, Math.ceil(localVotes / 10))
          }).map((_, i) => <span key={i} className="text-lg" data-id="element-122">
                🔥
              </span>)}
            <span className="ml-2 text-text-secondary font-semibold" data-id="element-123">
              {localVotes}
            </span>
          </div>
          <motion.button onClick={() => setShowReplies(!showReplies)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border-2 border-gray-700 hover:border-primary hover:bg-primary/10 hover:text-primary transition-all" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} data-id="element-124">
            <MessageCircleIcon className="h-5 w-5" data-id="element-125" />
            <span data-id="element-126">{replyCount}</span>
          </motion.button>
        </div>
        <span className="text-sm text-text-secondary" data-id="element-127">{relativeTime}</span>
      </div>
      <AnimatePresence data-id="element-128">
        {showReplies && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="mt-6" data-id="element-129">
            <div className="border-t border-gray-800 pt-6" data-id="element-130">
              <h3 className="text-sm font-bold text-text-secondary uppercase tracking-wide mb-4" data-id="element-131">
                Replies ({replyCount})
              </h3>
              {replies.length > 0 && <div className="space-y-3 mb-6" data-id="element-132">
                  {replies.map(reply => <ReplyCard key={reply.id} reply={reply} onReplyUpdated={loadReplies} data-id="element-133" />)}
                </div>}
              <form onSubmit={handleSubmitReply} data-id="element-134">
                <div className="flex gap-2" data-id="element-135">
                  <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Add your thoughts..." className="flex-1 px-4 py-3 bg-background text-text-primary border border-gray-700 rounded-lg focus:outline-none focus:border-primary transition-all placeholder-text-secondary" disabled={isSubmittingReply} data-id="element-136" />
                  <motion.button type="submit" className="px-6 py-3 bg-primary text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" whileHover={{
                scale: replyText.trim().length === 0 || isSubmittingReply ? 1 : 1.05,
                backgroundColor: replyText.trim().length === 0 || isSubmittingReply ? '#FF4500' : '#FF6A33'
              }} whileTap={{
                scale: replyText.trim().length === 0 || isSubmittingReply ? 1 : 0.95
              }} disabled={replyText.trim().length === 0 || isSubmittingReply} data-id="element-137">
                    <SendIcon className="h-4 w-4" data-id="element-138" />
                    {isSubmittingReply ? 'Sending...' : 'Reply'}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
};